import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../utils/api';
import { format } from 'date-fns';
import {
  UserX, UserCog, UserMinus, MailWarning, UserPlus, CalendarClock, FolderCog,
  Users, FolderKanban, CheckSquare, Bug, ShieldCheck, ScrollText, Settings,
  Database, BarChart3, SlidersHorizontal, ListTree, LayoutGrid,
} from 'lucide-react';

/**
 * The old dashboard opened with four counters — total users, total projects,
 * total tasks, open bugs. All inventory, none of it answering the question an
 * administrator actually arrives with: is anything wrong?
 *
 * This leads with what needs a person to act, and only then shows the counts.
 * Everything here links to the page where the thing is fixed, so a number is
 * never a dead end.
 */

const SAPPHIRE = '#2E539F';
const JAVA     = '#5CC3BC';
const SUNGLOW  = '#FFC849';
const DANGER   = '#ef4444';

/**
 * Severity, not decoration. Red is only for things that block someone or lose
 * data — the guideline reserves it for exactly that. Everything else is a
 * highlight, which is what Sunglow is for.
 */
const SEV = {
  blocking:  { bg: 'rgba(239,68,68,.10)',  fg: DANGER,    border: DANGER },
  attention: { bg: 'rgba(255,200,73,.18)', fg: '#8A6400', border: SUNGLOW },
  settled:   { bg: 'rgba(92,195,188,.14)', fg: '#2f8a84', border: JAVA },
};

export default function AdminDashboardPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    adminAPI.getDashboard()
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.message || 'Failed to load dashboard data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (error) return <div className="card text-center py-12"><p style={{ color: DANGER }}>{error}</p></div>;

  const u = data?.users || {}, p = data?.projects || {}, t = data?.tasks || {}, b = data?.bugs || {};
  const a = data?.attention || {};
  const logs = data?.recent_activity || [];

  /**
   * Ordered by how much it costs to leave alone, not by how large the number
   * is. A locked user cannot work at all; an unassigned task is merely untidy.
   */
  const attention = [
    { key:'deactivated_users',     n:a.deactivated_users,     icon:UserX,
      label:'Deactivated',         detail:'cannot sign in at all',
      sev:'blocking',  to:'/admin/users?filter=deactivated' },
    { key:'failed_emails_7d',      n:a.failed_emails_7d,      icon:MailWarning,
      label:'Failed emails',       detail:'in the last 7 days',
      sev:'blocking',  to:'/admin/email-log?status=failed' },
    { key:'overdue_tasks',         n:a.overdue_tasks,         icon:CalendarClock,
      label:'Overdue tasks',       detail:'past their due date',
      sev:'attention', to:'/tasks?overdue=1' },
    { key:'dormant_with_work',     n:a.dormant_with_work,     icon:UserMinus,
      label:'Dormant with work',   detail:'no sign-in for 30 days, open tasks assigned',
      sev:'attention', to:'/admin/users?filter=dormant' },
    { key:'unassigned_open_tasks', n:a.unassigned_open_tasks, icon:UserPlus,
      label:'Unassigned tasks',    detail:'open, with nobody to do them',
      sev:'attention', to:'/tasks?unassigned=1' },
    { key:'never_logged_in',       n:a.never_logged_in,       icon:UserCog,
      label:'Never signed in',     detail:'active accounts, never used',
      sev:'attention', to:'/admin/users?filter=never' },
    { key:'projects_no_owner',     n:a.projects_no_owner,     icon:FolderCog,
      label:'Projects with no roles', detail:'nobody assigned to them',
      sev:'attention', to:'/projects?no_roles=1' },
  ];

  const raised = attention.filter(x => Number(x.n) > 0);
  const clear  = attention.length - raised.length;

  const counts = [
    { label:'Users',    value:u.total,   sub:`${u.active ?? 0} active`,       icon:Users,        to:'/admin/users' },
    { label:'Projects', value:p.total,   sub:`${p.active ?? 0} active`,       icon:FolderKanban, to:'/projects' },
    { label:'Tasks',    value:t.total,   sub:`${t.completed ?? 0} completed`, icon:CheckSquare,  to:'/tasks' },
    { label:'Bugs',     value:b.open,    sub:`${b.total ?? 0} total`,         icon:Bug,          to:'/bugs' },
  ];

  const roles = [
    { role:'System Administrator', count:u.system_admins||0 },
    { role:'Administrator',        count:u.admins||0 },
    { role:'Executive',            count:u.executives||0 },
    { role:'Manager',              count:u.managers||0 },
    { role:'Specialist',           count:u.specialists||0 },
    { role:'Developer',            count:u.developers||0 },
  ];
  const roleMax = Math.max(1, ...roles.map(r => r.count));

  const shortcuts = [
    { to:'/admin/users',           label:'User Management',    icon:Users },
    { to:'/admin/role-management', label:'Role Management',    icon:ShieldCheck },
    { to:'/admin/page-access',     label:'Page Access',        icon:ListTree },
    { to:'/admin/masters',         label:'Masters',            icon:SlidersHorizontal },
    { to:'/admin/metadata',        label:'Custom Fields',      icon:SlidersHorizontal },
    { to:'/admin/platforms',       label:'Platforms',          icon:LayoutGrid },
    { to:'/admin/audit-logs',      label:'Audit Logs',         icon:ScrollText },
    { to:'/admin/analytics',       label:'Analytics',          icon:BarChart3 },
    { to:'/admin/data',            label:'Data Management',    icon:Database },
    { to:'/admin/settings',        label:'System Settings',    icon:Settings },
  ];

  const ACTION_COLOR = {
    CREATE:JAVA, UPDATE:SAPPHIRE, DELETE:DANGER, LOGIN:'#7F8C8D', LOGOUT:'#7F8C8D',
    APPROVE:JAVA, REJECT:DANGER, EXPORT:SAPPHIRE, USER_LOCKED:DANGER,
    USER_UNLOCKED:JAVA, ADMIN_PASSWORD_RESET:SUNGLOW,
    PASSWORD_RESET_VIA_TOKEN:SUNGLOW, USER_ROLE_CHANGED:SAPPHIRE,
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="page-title">Admin Dashboard</h1>
        <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
          What needs attention, then the numbers behind it
        </p>
      </div>

      {/* ── Needs attention ─────────────────────────────────────────────── */}
      {raised.length === 0 ? (
        <div className="card flex items-center gap-3 py-4"
          style={{ borderLeft:`4px solid ${JAVA}` }}>
          <ShieldCheck size={20} style={{ color:'#2f8a84' }} />
          <div>
            <p className="font-semibold text-sm" style={{ color:'var(--text)' }}>Nothing needs attention</p>
            <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
              All seven checks are clear: no locked accounts, no failed emails, no overdue
              or unassigned work, no dormant users holding tasks.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color:SAPPHIRE }}>
              Needs attention
            </h2>
            {clear > 0 && (
              <span className="text-xs" style={{ color:'var(--text-secondary)' }}>
                {clear} other check{clear === 1 ? '' : 's'} clear
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {raised.map(x => {
              const s = SEV[x.sev];
              const Icon = x.icon;
              return (
                <Link key={x.key} to={x.to}
                  className="card flex items-center gap-3 py-3 px-4 hover:shadow-md transition-shadow"
                  style={{ borderLeft:`4px solid ${s.border}` }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background:s.bg, color:s.fg }}>
                    <Icon size={17} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-bold leading-tight" style={{ color:'var(--text)' }}>
                      {x.n} <span className="text-sm font-semibold">{x.label}</span>
                    </p>
                    <p className="text-xs truncate" style={{ color:'var(--text-secondary)' }}>{x.detail}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Counts ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {counts.map(s => {
          const Icon = s.icon;
          return (
            <Link key={s.label} to={s.to} className="stat-card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <span className="stat-label">{s.label}</span>
                <Icon size={15} style={{ color:'var(--text-secondary)' }} />
              </div>
              <p className="text-3xl font-bold" style={{ color:'var(--text)' }}>{s.value ?? 0}</p>
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{s.sub}</p>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── Roles ───────────────────────────────────────────────────── */}
        <div className="card">
          <h3 className="font-semibold text-sm mb-1" style={{ color:'var(--text)' }}>People by permission level</h3>
          <p className="text-xs mb-4" style={{ color:'var(--text-secondary)' }}>
            Levels, not job titles. Several roles sit at each level.
          </p>
          <div className="space-y-2.5">
            {roles.map(r => (
              <div key={r.role}>
                <div className="flex justify-between text-xs mb-1">
                  <span style={{ color:'var(--text)' }}>{r.role}</span>
                  <span className="font-semibold" style={{ color:'var(--text-secondary)' }}>{r.count}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
                  <div className="h-full rounded-full"
                    style={{ width:`${(r.count / roleMax) * 100}%`, background:SAPPHIRE }} />
                </div>
              </div>
            ))}
          </div>
          <Link to="/admin/role-management"
            className="block mt-4 text-xs font-semibold" style={{ color:SAPPHIRE }}>
            Manage roles and permissions
          </Link>
        </div>

        {/* ── Recent activity ─────────────────────────────────────────── */}
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm" style={{ color:'var(--text)' }}>Recent activity</h3>
            <Link to="/admin/audit-logs" className="text-xs font-semibold" style={{ color:SAPPHIRE }}>
              Full audit log
            </Link>
          </div>
          {logs.length === 0 ? (
            <p className="text-sm py-6" style={{ color:'var(--text-secondary)' }}>
              Nothing recorded yet.
            </p>
          ) : (
            <div className="divide-y" style={{ borderColor:'var(--border)' }}>
              {logs.slice(0, 10).map((l, i) => (
                <div key={l.id || i} className="py-2 flex items-center gap-3 text-sm">
                  <span className="font-semibold text-xs flex-shrink-0 w-32 truncate"
                    style={{ color:ACTION_COLOR[l.action] || 'var(--text-secondary)' }}>
                    {l.action}
                  </span>
                  <span className="flex-1 truncate" style={{ color:'var(--text)' }}>
                    {l.user_name || 'System'}
                    {l.entity_type ? ` · ${l.entity_type}` : ''}
                  </span>
                  <span className="text-xs flex-shrink-0" style={{ color:'var(--text-secondary)' }}>
                    {l.created_at ? format(new Date(l.created_at), 'd MMM HH:mm') : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Shortcuts ───────────────────────────────────────────────────── */}
      <div className="card">
        <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Admin tools</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {shortcuts.map(s => {
            const Icon = s.icon;
            return (
              <Link key={s.to} to={s.to}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors"
                style={{ background:'var(--surface-alt)', color:'var(--text)' }}>
                <Icon size={15} style={{ color:SAPPHIRE }} />
                <span className="truncate">{s.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
